import React, { useEffect, useMemo, useState } from 'react';
import './EventsPage.css';

const formatDateTime = (value) => {
  if (!value) return 'TBD';
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'TBD';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const buildCalendarUrl = (event) => {
  const start = event.startTime?.toDate ? event.startTime.toDate() : new Date(event.startTime);
  const end = event.endTime?.toDate ? event.endTime.toDate() : new Date(event.endTime);
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

  const format = (date) =>
    date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || 'Event',
    dates: `${format(start)}/${format(end)}`,
    details: event.description || 'Event',
    location: event.location || '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const EventsPage = ({ firebase }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firebase) return undefined;
    const unsubscribe = firebase.db
      .collection('academyEvents')
      .orderBy('startTime', 'asc')
      .onSnapshot(
        (snapshot) => {
          const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setEvents(items);
          setLoading(false);
        },
        () => setLoading(false),
      );

    return () => unsubscribe();
  }, [firebase]);

  const now = Date.now();
  const upcoming = useMemo(
    () =>
      events.filter((event) => {
        const start = event.startTime?.toDate ? event.startTime.toDate() : new Date(event.startTime);
        return start && !Number.isNaN(start.getTime()) && start.getTime() >= now;
      }),
    [events, now],
  );
  const past = useMemo(
    () =>
      events.filter((event) => {
        const start = event.startTime?.toDate ? event.startTime.toDate() : new Date(event.startTime);
        return start && !Number.isNaN(start.getTime()) && start.getTime() < now;
      }),
    [events, now],
  );

  return (
    <div className="events-page">
      <div className="events-header">
        <div>
          <h2>Events</h2>
          <p>Stay updated on upcoming events and franchise meets.</p>
        </div>
      </div>

      <div className="events-section">
        <h3>Upcoming Events</h3>
        {loading ? (
          <div className="waiting-card">Loading events…</div>
        ) : upcoming.length === 0 ? (
          <div className="waiting-card">No upcoming events yet.</div>
        ) : (
          <div className="event-list">
            {upcoming.map((event) => (
              <div className="event-card" key={event.id}>
                <div>
                  <h4>{event.title || 'Event'}</h4>
                  <p>{formatDateTime(event.startTime)}</p>
                  {event.description ? <p className="event-desc">{event.description}</p> : null}
                </div>
                <div className="event-actions">
                  <button type="button">Book Your Spot</button>
                  {buildCalendarUrl(event) ? (
                    <a href={buildCalendarUrl(event)} target="_blank" rel="noreferrer">
                      Add to Calendar
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="events-section">
        <h3>Past Events</h3>
        {past.length === 0 ? (
          <div className="waiting-card">No past events.</div>
        ) : (
          <div className="event-list past">
            {past.map((event) => (
              <div className="event-card" key={event.id}>
                <div>
                  <h4>{event.title || 'Event'}</h4>
                  <p>{formatDateTime(event.startTime)}</p>
                </div>
                <div className="event-actions">
                  {buildCalendarUrl(event) ? (
                    <a href={buildCalendarUrl(event)} target="_blank" rel="noreferrer">
                      Add to Calendar
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
